import { useEffect, useRef, useState, type ReactNode } from 'react';

export function Pill({ children, tone }: { children: ReactNode; tone?: 'live' | 'paper' | 'warn' | undefined }) {
  return <span className={`pill${tone ? ` ${tone}` : ''}`}>{children}</span>;
}

export function KV({ k, v, tone }: { k: string; v: ReactNode; tone?: 'gain' | 'loss' | 'warn' | undefined }) {
  return (
    <div className="kv">
      <div className="k">{k}</div>
      <div className={`v${tone ? ` ${tone}` : ''}`}>{v}</div>
    </div>
  );
}

export function Sheet({ open, onClose, children, title }: { open: boolean; onClose: () => void; children: ReactNode; title?: string | undefined }) {
  if (!open) return null;
  return (
    <div className="sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="handle" />
        {title && <div className="t-h" style={{ marginBottom: 12 }}>{title}</div>}
        {children}
      </div>
    </div>
  );
}

/** Long-press to confirm (docs/design/information-architecture.md activation). */
export function HoldButton({ onConfirm, children, ms = 1200, className = 'btn primary block', disabled }: { onConfirm: () => void; children: ReactNode; ms?: number; className?: string; disabled?: boolean | undefined }) {
  const [holding, setHolding] = useState(false);
  const timer = useRef<number | null>(null);
  const start = () => {
    if (disabled) return;
    setHolding(true);
    timer.current = window.setTimeout(() => {
      setHolding(false);
      onConfirm();
    }, ms);
  };
  const stop = () => {
    setHolding(false);
    if (timer.current) window.clearTimeout(timer.current);
  };
  return (
    <button className={`${className} hold`} onPointerDown={start} onPointerUp={stop} onPointerLeave={stop} onPointerCancel={stop} disabled={disabled}>
      <span className="fill" style={{ width: holding ? '100%' : 0, transitionDuration: holding ? `${ms}ms` : '0ms' }} />
      <span style={{ position: 'relative' }}>{holding ? 'Hold…' : children}</span>
    </button>
  );
}

export function Toast({ message, onDone }: { message: string | null; onDone: () => void }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onDone, 2800);
    return () => clearTimeout(t);
  }, [message, onDone]);
  if (!message) return null;
  return <div className="toast">{message}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}
